import moment from 'moment';
import { AddCommasToAmount } from '../../helper/Utils';

export const TABLE_HEADER_HEIGHT = 56;
export const TABLE_PAGINATION_HEIGHT = 56;
export const TABLE_BOTTOM_GAP = 24;

/** Online rows with payment_date before this date can also be selected for challan (like offline). */
export const ONLINE_CHALLAN_PAYMENT_DATE_BEFORE = '2026-09-01';

export const isOfflineRow = (row) =>
    String(row?.payment_mode || '').toLowerCase() === 'offline';

export const isOnlineRow = (row) =>
    String(row?.payment_mode || '').toLowerCase() === 'online';

/** payment_date set ⇒ amount credited to beneficiary account */
export const hasPaymentDate = (payment_date) => {
    if (payment_date == null || payment_date === '') return false;
    const value = String(payment_date).trim();
    if (!value || value.startsWith('0000-00-00')) return false;
    return true;
};

/** Checkbox / Add Challan: offline, or online with payment_date older than 1 Sep 2026 */
export const isSelectableForChallan = (row) => {
    if (isOfflineRow(row)) return true;
    if (!isOnlineRow(row) || !hasPaymentDate(row?.payment_date)) return false;
    const paymentDate = moment(
        row.payment_date,
        ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601],
        true
    );
    if (!paymentDate.isValid()) return false;
    return paymentDate.isBefore(
        moment(ONLINE_CHALLAN_PAYMENT_DATE_BEFORE, 'YYYY-MM-DD'),
        'day'
    );
};

/**
 * Online payment status for list view.
 * Offline: no badge. Priority: payment_date → Success; else flag_success_return;
 * null/empty flag → Pending.
 */
export const getListPaymentStatus = (row) => {
    const mode = String(row?.payment_mode || '').toLowerCase();
    if (mode !== 'online') return null;

    if (hasPaymentDate(row?.payment_date)) {
        return { label: 'Success', color: '#d4edda', textColor: '#155724' };
    }

    const flag =
        row?.flag_success_return == null
            ? null
            : String(row.flag_success_return).trim();

    if (flag === '1') {
        return { label: 'Success', color: '#d4edda', textColor: '#155724' };
    }
    if (flag === '0') {
        return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
    }
    if (flag === '2') {
        return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
    }
    if (flag === '3') {
        return { label: 'On Hold', color: '#fff3cd', textColor: '#856404' };
    }
    if (flag === null || flag === '') {
        return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
    }
    return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
};

/** Payment trail modal status */
export const getPaymentStatus = (payment_mode, flag_success_return, payment_date) => {
    if (hasPaymentDate(payment_date)) {
        return { label: 'Success', color: '#d4edda', textColor: '#155724' };
    }

    const mode = String(payment_mode || '').toLowerCase();
    const flag =
        flag_success_return == null ? null : String(flag_success_return).trim();

    if (mode === 'online') {
        if (flag === '1') {
            return { label: 'Success', color: '#d4edda', textColor: '#155724' };
        }
        if (flag === '0') {
            return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
        }
        if (flag === null || flag === '') {
            return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
        }
        if (flag === '2') {
            return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
        }
        if (flag === '3') {
            return { label: 'On Hold', color: '#fff3cd', textColor: '#856404' };
        }
        return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
    }
    return { label: 'Success', color: '#d4edda', textColor: '#155724' };
};

export const isHoldingTrailStep = (trail) => {
    const stage = String(trail?.trail_stage || '').toLowerCase().trim();
    if (
        stage === 'holding_transfer' ||
        stage === 'holding' ||
        stage.includes('holding')
    ) {
        return true;
    }

    const txn = String(trail?.transaction_reference || '').toUpperCase();
    if (txn.startsWith('HOLD-QUEUE-') || txn.startsWith('HOLD-')) {
        return true;
    }

    return false;
};

/**
 * List badge "Trf to holding" when holding ACH credit for this invoice succeeded.
 * Prefer holding_flag_success_return (same as holding settlement details).
 */
export const isHoldingTransferSuccess = (row) => {
    const flag =
        row?.holding_flag_success_return ??
        row?.holding_transfer_flag_success_return ??
        row?.trf_to_holding;

    if (flag === true || flag === 1 || String(flag).trim() === '1') return true;

    const boolFlag = row?.is_trf_to_holding ?? row?.is_transferred_to_holding;
    if (boolFlag === true || boolFlag === 1 || String(boolFlag).trim() === '1') {
        return true;
    }

    return false;
};

/**
 * TDS-IT Add Challan selection:
 * - Enabled only after user picks a TDS section in the header dropdown
 *   (so one section at a time)
 * - Row selectable if previous challan rules pass OR holding transfer succeeded
 */
export const isSelectableForTdsItChallan = (row, tdsSectionId) => {
    if (!tdsSectionId) return false;
    return isSelectableForChallan(row) || isHoldingTransferSuccess(row);
};

/**
 * GST Add Challan selection (no section filter):
 * offline, online with payment_date before 1 Sep 2026, or holding transfer succeeded.
 */
export const isSelectableForGstChallan = (row) =>
    isSelectableForChallan(row) || isHoldingTransferSuccess(row);

export const formatTrailAmount = (amountPaise) => {
    if (amountPaise == null || amountPaise === '') return 'N/A';
    const paise = Number(amountPaise);
    if (Number.isNaN(paise)) return String(amountPaise);
    return AddCommasToAmount(paise / 100);
};

/** Short remarks like beneficiary "Payment Successful" */
export const getCrispHoldingRemark = (raw) => {
    const text = String(raw || '').toLowerCase();
    if (!text) return 'Queued';
    if (text.includes('queued')) return 'Queued';
    if (text.includes('file generated') || text.includes('sent to bank')) {
        return 'Sent To Bank';
    }
    if (text.includes('success') || text.includes('credited')) {
        return 'Transfer Successful';
    }
    if (text.includes('reject')) return 'Rejected';
    if (text.includes('fail')) return 'Failed';
    if (text.includes('hold')) return 'On Hold';
    return 'Holding Transfer';
};

/** Success wording belongs only on a Success step. The first payment row is shown as Pending. */
const isSuccessOnlyRemark = (text) =>
    /payment\s+successful|^success(ful)?$|credited/i.test(String(text || '').trim());

export const formatTrailRemarks = (trail, index) => {
    const text = String(trail?.remarks || trail?.reason || '').trim();
    if (isHoldingTrailStep(trail)) {
        return getCrispHoldingRemark(text);
    }
    const status = getTrailStepStatus(trail, index);
    if (index === 0 && (!text || (status?.label !== 'Success' && isSuccessOnlyRemark(text)))) {
        return 'Sent To Bank';
    }
    if (status?.label !== 'Success' && isSuccessOnlyRemark(text)) {
        return 'N/A';
    }
    return text || 'N/A';
};

export const getTrailRemarksTitle = (trail) => {
    if (!isHoldingTrailStep(trail)) return undefined;
    const text = String(trail?.remarks || trail?.reason || '').trim();
    return text || undefined;
};

export const getTrailStepStatus = (trail, index) => {
    if (!isHoldingTrailStep(trail) && index === 0) {
        return getPaymentStatus('online', null);
    }
    return getPaymentStatus('online', trail?.flag_success_return);
};

export const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};
